import React from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../../components/Logo/Logo';
import './Landing.css';

const Hero: React.FC = () => (
  <section className="hero">
    <div className="hero-content">
      <h1>Gerencie seus leads com <span className="highlight">inteligência</span></h1>
      <p className="hero-subtitle">
        CRM Kanban com integração WhatsApp. Simples, poderoso e com o melhor preço do mercado.
      </p>
      <div className="hero-cta">
        <Link to="/register" className="btn btn-primary btn-lg">
          Começar Grátis
        </Link>
        <Link to="/pricing" className="btn btn-outline btn-lg">
          Ver Preços
        </Link>
      </div>
      <p className="hero-note">Sem cartão de crédito. Cancele quando quiser.</p>
    </div>
    <div className="hero-image">
      <div className="hero-mockup">
        <div className="mockup-header">
          <span className="mockup-dot red"></span>
          <span className="mockup-dot yellow"></span>
          <span className="mockup-dot green"></span>
        </div>
        <div className="mockup-content">
          <div className="mockup-kanban">
            <div className="mockup-column">
              <div className="mockup-column-header">Entrada</div>
              <div className="mockup-card"></div>
              <div className="mockup-card"></div>
            </div>
            <div className="mockup-column">
              <div className="mockup-column-header">Proposta</div>
              <div className="mockup-card active"></div>
            </div>
            <div className="mockup-column">
              <div className="mockup-column-header">Fechamento</div>
              <div className="mockup-card"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Features: React.FC = () => {
  const features = [
    {
      icon: '📊',
      title: 'Kanban Visual',
      description: 'Arraste e solte leads entre colunas. Visualize todo seu funil de vendas em um único lugar.'
    },
    {
      icon: '💬',
      title: 'WhatsApp Integrado',
      description: 'Envie e receba mensagens diretamente do CRM. Histórico completo de conversas.'
    },
    {
      icon: '👥',
      title: 'Multi-usuários',
      description: 'Adicione sua equipe. Cada atendente vê apenas seus leads, admins veem tudo.'
    },
    {
      icon: '📎',
      title: 'Anexos e Documentos',
      description: 'Anexe propostas, contratos e documentos aos leads. Tudo organizado.'
    },
    {
      icon: '🔔',
      title: 'Notificações',
      description: 'Receba alertas de novas mensagens e leads. Nunca perca uma oportunidade.'
    },
    {
      icon: '📈',
      title: 'Relatórios',
      description: 'Acompanhe métricas de conversão, tempo de resposta e performance da equipe.'
    }
  ];

  return (
    <section className="features" id="features">
      <div className="container">
        <h2 className="section-title">Tudo que você precisa para vender mais</h2>
        <p className="section-subtitle">
          Ferramentas poderosas em uma interface simples e intuitiva
        </p>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PricingPreview: React.FC = () => {
  const plans = [
    { name: 'Free', users: 1, price: 0, popular: false },
    { name: 'Starter', users: 3, price: 49, popular: true },
    { name: 'Business', users: 10, price: 199, popular: false },
    { name: 'Enterprise', users: 'Personalizados', price: -1, popular: false } // -1 = Sob consulta
  ];

  return (
    <section className="pricing-preview" id="pricing">
      <div className="container">
        <h2 className="section-title">Preços que cabem no seu bolso</h2>
        <p className="section-subtitle">
          Enquanto outros cobram R$200+, nós acreditamos em preço justo
        </p>
        <div className="pricing-grid">
          {plans.map((plan, index) => (
            <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
              {plan.popular && <span className="popular-badge">Mais Popular</span>}
              <h3>{plan.name}</h3>
              <div className="pricing-price">
                {plan.price < 0 ? (
                  <span className="amount">Sob consulta</span>
                ) : (
                  <>
                    <span className="currency">R$</span>
                    <span className="amount">{plan.price}</span>
                    <span className="period">/mês</span>
                  </>
                )}
              </div>
              <p className="pricing-users">{plan.price < 0 ? 'Limites personalizados' : typeof plan.users === 'number' ? `Até ${plan.users} usuários` : `${plan.users} usuários`}</p>
              <Link 
                to={`/register?plan=${plan.name.toLowerCase()}`} 
                className={`btn ${plan.popular ? 'btn-primary' : 'btn-outline'} btn-block`}
              >
                {plan.price === 0 ? 'Começar Grátis' : plan.price < 0 ? 'Fale Conosco' : 'Escolher Plano'}
              </Link>
            </div>
          ))}
        </div>
        <div className="pricing-cta">
          <Link to="/pricing" className="link-arrow">
            Ver comparação completa de planos →
          </Link>
        </div>
      </div>
    </section>
  );
};

const Testimonials: React.FC = () => {
  const testimonials = [
    {
      name: 'Maria Silva',
      role: 'Corretora de Imóveis',
      text: 'Antes eu perdia leads por não responder a tempo. Com o Ghi Connect, tudo fica organizado e não perco mais nenhuma oportunidade.',
      avatar: 'MS'
    },
    {
      name: 'João Santos',
      role: 'Dono de Academia',
      text: 'O preço é imbatível. Pagava R$300 em outro CRM que fazia menos. Aqui pago R$49 e minha equipe usa tranquilo.',
      avatar: 'JS'
    },
    {
      name: 'Ana Oliveira',
      role: 'Consultora de Vendas',
      text: 'A integração com WhatsApp mudou minha vida. Respondo clientes sem sair do CRM e tenho todo o histórico salvo.',
      avatar: 'AO'
    }
  ];

  return (
    <section className="testimonials">
      <div className="container">
        <h2 className="section-title">O que nossos clientes dizem</h2>
        <div className="testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="testimonial-card">
              <p className="testimonial-text">"{testimonial.text}"</p>
              <div className="testimonial-author">
                <div className="testimonial-avatar">{testimonial.avatar}</div>
                <div>
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.role}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTA: React.FC = () => (
  <section className="cta">
    <div className="container">
      <h2>Pronto para vender mais?</h2>
      <p>Comece grátis hoje. Sem compromisso, sem cartão de crédito.</p>
      <Link to="/register" className="btn btn-white btn-lg">
        Criar Conta Grátis
      </Link>
    </div>
  </section>
);

const Footer: React.FC = () => (
  <footer className="footer">
    <div className="container">
      <div className="footer-grid">
        <div className="footer-brand">
          <h3>Ghi Connect</h3>
          <p>CRM Kanban com WhatsApp integrado para equipes que querem vender mais.</p>
        </div>
        <div className="footer-links">
          <h4>Produto</h4>
          <ul>
            <li><a href="#features">Funcionalidades</a></li>
            <li><Link to="/pricing">Preços</Link></li>
            <li><a href="#integrations">Integrações</a></li>
          </ul>
        </div>
        <div className="footer-links">
          <h4>Empresa</h4>
          <ul>
            <li><a href="#about">Sobre</a></li>
            <li><a href="#contact">Contato</a></li>
            <li><a href="#blog">Blog</a></li>
          </ul>
        </div>
        <div className="footer-links">
          <h4>Legal</h4>
          <ul>
            <li><a href="#privacy">Privacidade</a></li>
            <li><a href="#terms">Termos de Uso</a></li>
          </ul>
        </div>
      </div>
      <div className="footer-bottom">
        <p>&copy; 2026 Ghi Connect. Todos os direitos reservados.</p>
      </div>
    </div>
  </footer>
);

const Navbar: React.FC = () => (
  <nav className="navbar">
    <div className="container navbar-content">
      <Link to="/" className="navbar-brand">
        <Logo size="small" showText={false} />
      </Link>
      <div className="navbar-links">
        <a href="#features">Funcionalidades</a>
        <Link to="/pricing">Preços</Link>
        <a href="#contact">Contato</a>
      </div>
      <div className="navbar-actions">
        <Link to="/login" className="btn btn-ghost">Entrar</Link>
        <Link to="/register" className="btn btn-primary">Criar Conta</Link>
      </div>
    </div>
  </nav>
);

export const Landing: React.FC = () => {
  return (
    <div className="landing-page">
      <Navbar />
      <Hero />
      <Features />
      <PricingPreview />
      <Testimonials />
      <CTA />
      <Footer />
    </div>
  );
};
